import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, OnDestroy, OnInit, Signal } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { FromDictionaryPipe, LanguageService } from '@teamfoster/sdk/dictionary-ngrx';
import { DynamicFormField } from '@teamfoster/sdk/legacy-dynamic-form';
import { Breadcrumb } from 'src/app/core/models';
import { combineLatest, first, firstValueFrom, map, Observable, of } from 'rxjs';
import { getUser } from 'src/app/auth/store';
import { getRouterState, SetTitle } from 'src/app/store';
import { Theme } from 'src/app/themes/models';
import { getOrderedThemes } from 'src/app/themes/store';
import { Event, EventData, EventDataDateSpan, UpdateEventData } from '../../models';
import * as navActions from '../../../store/actions/nav.action';
import { getSelectedEvent, getEventSaving, getEventSaved, UpdateEvent, CreateEvent, DeleteEvent } from '../../store';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmComponent } from 'src/app/core/components/confirm/confirm.component';
import { Initiative } from 'src/app/initiatives/models';
import { getMyOrderedInitiatives } from 'src/app/initiatives/store';

@Component({
  selector: 'app-event-form',
  templateUrl: './event-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventFormComponent implements OnInit, OnDestroy {
  prefix = 'evenement-formulier';
  breadcrumbs$: Observable<Breadcrumb[]> = of([]);

  usr$!: Observable<any>;
  event$!: Observable<Event>;
  themes$!: Observable<Theme[]>;
  initiatives$!: Observable<Initiative[]>;
  formFields$!: Observable<DynamicFormField[] | null>;
  formLoading$: Observable<boolean> = of(false);
  formLoaded$: Observable<boolean> = of(false);
  formError$?: Observable<any>;
  isNew$!: Observable<boolean>;
  id = 0;
  editMode = true;
  dateSpans: EventDataDateSpan[] = [];
  activeStep = 2;

  fieldsets?: any[];

  toolbarOptions = [
    ['bold', 'italic', 'underline', 'strike', 'link'], // toggled buttons
    ['blockquote'],

    [{ header: [2, 3, 4] }], // custom button values
    [{ list: 'ordered' }, { list: 'bullet' }],
    //[{ script: 'sub' }, { script: 'super' }], // superscript/subscript
    //[{ indent: '-1' }, { indent: '+1' }], // outdent/indent
    //[{ direction: 'rtl' }], // text direction

    //[{ size: ['small', false, 'large', 'huge'] }], // custom dropdown
    //[{ header: [2, false] }],

    //[{ color: [] }, { background: [] }], // dropdown with defaults from theme
    //[{ font: [] }],
    //[{ align: [] }],
    ['video'],
    ['clean'], // remove formatting button
  ];

  private dict = new FromDictionaryPipe(this.lang);

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private lang: LanguageService,
    private cd: ChangeDetectorRef,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.store.dispatch(navActions.SetTheme({ theme: 'palette-dark' }));
    this.usr$ = this.store.select(getUser);
    this.event$ = this.store.select(getSelectedEvent);
    this.formLoading$ = this.store.select(getEventSaving);
    this.formLoaded$ = this.store.select(getEventSaved);
    this.themes$ = this.store.select(getOrderedThemes);
    this.initiatives$ = this.store.select(getMyOrderedInitiatives);
    this.isNew$ = this.event$.pipe(map(Event => !Event || Event.id <= 0));
    this.formFields$ = combineLatest([this.usr$, this.event$, this.themes$, this.store.select(getRouterState), this.initiatives$]).pipe(
      map(([usr, event, themes, router, initiatives]) => {
        let init = {};
        if (router.state.params['eventId'] && !event) {
          return null;
        }

        if (themes === null) {
          return null;
        }

        if (event && usr.id !== event.createdById && usr.roles.map((role: string) => role.toUpperCase()).indexOf('ADMINISTRATOR') < 0) {
          this.editMode = false;

          this.cd.detectChanges();
          return null;
        }

        this.id = event?.id;

        this.dateSpans = event?.dateSpans || [];

        if (this.id) {
          this.store.dispatch(
            SetTitle({
              title: `${this.dict.transform('evenement-formulier-bewerk-pagina-titel')} - ${this.dict.transform('meta-titel-suffix')}`,
            })
          );
        } else {
          this.store.dispatch(
            SetTitle({
              title: `${this.dict.transform('evenement-formulier-nieuw-pagina-titel')} - ${this.dict.transform('meta-titel-suffix')}`,
            })
          );
        }

        const introLength = +this.dict.transform(`${this.prefix}-veld-maxlength-intro`);
        return [
          {
            label: 'id',
            name: 'id',
            fieldType: 'hidden',
            cssClass: 'd-none',
            validators: [],
            order: 0,
            value: event?.id || null,
          },
          {
            inputType: 'text',
            label: this.dict.transform(`${this.prefix}-veld-label-titel`),
            name: 'title',
            validators: [Validators.required],
            value: event?.title,
            placeholder: this.dict.transform(`${this.prefix}-veld-placeholder-titel`),
            fieldType: 'input',
            order: 0,
            fieldset: 'about',
          },
          {
            inputType: 'text',
            label: this.dict.transform(`${this.prefix}-veld-label-subtitel`),
            name: 'subtitle',
            validators: [],
            value: event?.subtitle,
            placeholder: this.dict.transform(`${this.prefix}-veld-placeholder-subtitel`),
            fieldType: 'input',
            order: 0,
            fieldset: 'about',
          },
          {
            inputType: 'text',
            label: this.dict.transform(`${this.prefix}-veld-label-intro`),
            name: 'intro',
            validators: [Validators.required, Validators.maxLength(introLength)],
            hint: this.dict.transform(`${this.prefix}-veld-hint-intro`),
            value: event?.intro,
            placeholder: this.dict.transform(`${this.prefix}-veld-placeholder-intro`),
            fieldType: 'textarea',
            order: 0,
            fieldset: 'about',
          },
          {
            label: this.dict.transform(`${this.prefix}-veld-label-tekst`),
            name: 'text',
            validators: [Validators.required],
            placeholder: this.dict.transform(`${this.prefix}-veld-placeholder-tekst`),
            value: event?.text,
            fieldType: 'wysiwyg',
            order: 0,
            fieldset: 'about',
            toolbar: this.toolbarOptions,
          },
          {
            label: this.dict.transform(`${this.prefix}-veld-label-afbeelding`),
            name: 'image',
            validators: [Validators.required],
            placeholder: this.dict.transform(`${this.prefix}-veld-placeholder-afbeelding`),
            value: event?.image?.url,
            fieldType: 'image',
            order: 0,
            accept: 'image/*',
            acceptHint: this.dict.transform(`${this.prefix}-veld-type-hint-afbeelding`, false),
            fieldset: 'about',
          },
          {
            inputType: 'text',
            label: this.dict.transform(`${this.prefix}-veld-label-afbeelding-alt-tekst`),
            name: 'imageAltText',
            validators: [Validators.required],
            placeholder: this.dict.transform(`${this.prefix}-veld-placeholder-afbeelding-alt-tekst`),
            value: event?.imageAltText,
            fieldType: 'input',
            order: 0,
            fieldset: 'about',
            hint: this.dict.transform(`${this.prefix}-veld-placeholder-afbeelding-alt-hulptekst`),
          },
          {
            inputType: 'text',
            label: this.dict.transform(`${this.prefix}-veld-label-afbeelding-caption-tekst`),
            name: 'imageCaption',
            validators: [Validators.required],
            placeholder: this.dict.transform(`${this.prefix}-veld-placeholder-afbeelding-caption-tekst`),
            value: event?.image.caption,
            fieldType: 'input',
            order: 0,
            fieldset: 'about',
            hint: this.dict.transform(`${this.prefix}-veld-placeholder-afbeelding-caption-hulptekst`),
          },
          {
            label: this.dict.transform(`${this.prefix}-veld-label-initiatief`),
            name: 'initiativeId',
            validators: [],
            placeholder: this.dict.transform(`${this.prefix}-veld-placeholder-initiatief`),
            fieldType: initiatives.length > 0 ? 'select' : 'hidden',
            order: 0,
            fieldset: 'event',
            value: event?.initiativeId || null,
            selectOptions: [{ title: 'Selecteer een initiatief', id: undefined }, ...initiatives.map(a => ({ title: a.title, id: a.id }))],
          },
          {
            inputType: 'text',
            label: this.dict.transform(`${this.prefix}-veld-label-meerdere-datums`),
            name: 'dateSpans',
            validators: [Validators.required],
            placeholder: this.dict.transform(`${this.prefix}-veld-placeholder-meerdere-datums`),
            value: event?.dateSpans,
            fieldType: 'multiple-dates',
            order: 0,
            fieldset: 'event',
          },
          {
            inputType: 'text',
            label: this.dict.transform(`${this.prefix}-veld-label-externe-link`),
            name: 'externalUrl',
            validators: [],
            placeholder: this.dict.transform(`${this.prefix}-veld-placeholder-externe-link`),
            value: event?.externalUrl,
            fieldType: 'input',
            order: 0,
            fieldset: 'event',
          },
          {
            inputType: 'text',
            label: this.dict.transform(`${this.prefix}-veld-label-externe-link-tekst`),
            name: 'externalUrlText',
            validators: [],
            placeholder: this.dict.transform(`${this.prefix}-veld-placeholder-externe-link-tekst`),
            value: event?.externalUrlText,
            fieldType: 'input',
            order: 0,
            fieldset: 'event',
          },
          {
            inputType: 'text',
            label: this.dict.transform(`${this.prefix}-veld-label-themas`),
            name: 'themes',
            validators: [Validators.required],
            placeholder: this.dict.transform(`${this.prefix}-veld-placeholder-themas`),
            fieldType: 'themes',
            order: 0,
            value: [...(event?.themes || [])],
            selectOptions: [...themes],
            fieldset: 'extra',
          },
          {
            inputType: 'checkbox',
            label: this.dict.transform(`${this.prefix}-veld-label-locatie-is-ijssel`),
            name: 'entireArea',
            validators: [],
            fieldType: 'hidden',
            order: 0,
            fieldset: 'extra',
            value: event?.entireArea || false,
          },
          {
            inputType: 'text',
            label: this.dict.transform(`${this.prefix}-veld-label-locatie`),
            name: 'locations',
            validators: [],
            placeholder: this.dict.transform(`${this.prefix}-veld-placeholder-locatie`),
            fieldType: 'location',
            order: 1,
            fieldset: 'extra',
            value: [...(event?.locations || [])],
          },
          // Divider to make date span picker inside of the form, does not really work
          // {
          //   fieldType: 'divider',
          //   order: 1,
          //   label: '',
          //   intro: `<div class="date-span-placeholder"></div>`,
          //   validators: [],
          //   name: 'divider',
          //   cssClass: 'register-divider ',
          //   fieldset: 'event',
          // },
        ];
      })
    );

    this.fieldsets = [
      {
        name: 'intro',
        title: this.dict.transform(`${this.prefix}-stap-intro-titel`),
        description: this.dict.transform(`${this.prefix}-stap-intro-tekst`, false),
      },
      {
        name: 'about',
        title: this.dict.transform(`${this.prefix}-stap-basisgegevens-titel`),
        description: this.dict.transform(`${this.prefix}-stap-basisgegevens-tekst`, false),
      },
      {
        name: 'event',
        title: this.dict.transform(`${this.prefix}-stap-evenementgegevens-titel`),
        description: this.dict.transform(`${this.prefix}-stap-evenementgegevens-tekst`, false),
      },
      {
        name: 'extra',
        title: this.dict.transform(`${this.prefix}-stap-meta-titel`),
        description: this.dict.transform(`${this.prefix}-stap-meta-tekst`, false),
      },
    ];

    this.breadcrumbs$ = this.event$.pipe(
      map(i => {
        let crumbs = [
          {
            routerLink: ['/activiteiten'],
            title: this.dict.transform('breadcrumbs-evenementen-overzicht-label'),
          },
          {
            title: 'Nieuwe activiteit',
          },
        ];

        if (i) {
          crumbs = [
            {
              routerLink: ['/activiteiten'],
              title: this.dict.transform('breadcrumbs-evenementen-overzicht-label'),
            },
            {
              routerLink: i.url,
              title: i.title,
            },
            {
              title: 'Bewerken',
            },
          ];
        }

        return crumbs;
      })
    );
  }

  handleStepChange(step: number) {
    this.activeStep = step;
  }

  handleSubmit(data: any) {
    const eventData: EventData = {
      title: data.title,
      subtitle: data.subtitle,
      intro: data.intro,
      text: data.text,
      image: data.image,
      imageAltText: data.imageAltText,
      imageCaption: data.imageCaption,
      initiativeId: data.initiativeId,
      locations: data.locations,
      dateSpans: data.dateSpans,
      externalUrl: data.externalUrl,
      externalUrlText: data.externalUrlText,
      themes: data.themes,
      entireArea: data.entireArea,
    };

    if (data['id'] > 0) {
      const update: UpdateEventData = { ...eventData, id: data.id };
      this.store.dispatch(UpdateEvent({ id: data['id'], eventData: update }));
      return;
    }

    this.store.dispatch(CreateEvent({ eventData: eventData }));
  }

  ngOnDestroy(): void {
    this.store.dispatch(navActions.SetTheme({ theme: 'palette-light' }));
  }

  async deleteEvent(event: Event) {
    const dialogRefForm = this.dialog.open(ConfirmComponent, {
      maxHeight: '50vh',
      width: '90vw',
      maxWidth: '500px',
      data: {
        title: `${this.dict.transform('verwijder-evenement-waarschuwing-titel')}`,
        message: `${this.dict.transform('verwijder-evenement-waarschuwing-tekst')}`,
      },
      disableClose: false,
    });

    const result = await firstValueFrom(dialogRefForm.afterClosed());
    if (result) {
      this.store.dispatch(DeleteEvent({ id: event.id }));
    }
  }
}
